import { sendRegisterUser } from "@/models/socket/register.model";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import { AUTH_METHODS, RegisterUser } from "@/types";
import { ValidateError, validateRegistration } from "@/utils/validation.util";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

type RegisterProps = {
  setSection: React.Dispatch<React.SetStateAction<"login" | "register">>;
};

const Register: React.FC<RegisterProps> = (props) => {
  const { t } = useTranslation();
  const [newUser, setNewUser] = useState<RegisterUser>({
    fullName: "",
    email: "",
    password: "",
    phoneNumber: "",
    companyName: "",
    authMethod: "Google",
  });
  const [errors, setErrors] = useState<ValidateError<RegisterUser>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  async function handleRegister() {
    setErrors({});
    setSubmitSuccess(false);
    const validated = validateRegistration(newUser);
    if (!validated.isValid) {
      setErrors(validated.errors);
      return;
    }

    setSubmitting(true);
    const response = await sendRegisterUser(newUser);
    setSubmitting(false);
    if (response.rStatus === "error") {
      Swal.fire({
        icon: "error",
        title: t("Registration Failed"),
        text: response.rData,
      });
      return;
    }

    setSubmitSuccess(true);
  }

  return !submitSuccess ? (
    <>
      <div className="grid w-full gap-2">
        <div className="grid grid-cols-2 items-center gap-2 gap-x-4">
          <Label
            htmlFor="fullName"
            className="m-auto mr-0 text-base font-semibold"
          >
            <span>
              {t("Full Name")}
              <span className="ml-0.5 text-red-500 opacity-70">*</span>
            </span>
          </Label>
          <Input
            type="text"
            className="w-full p-1 ps-2"
            id="fullName"
            placeholder={t("Enter full name")}
            value={newUser.fullName}
            onChange={(e) =>
              setNewUser({ ...newUser, fullName: e.target.value })
            }
            autoCapitalize="off"
            required
          />
          {errors.fullName && (
            <div className="col-span-2 -mt-2 text-sm text-red-500">
              {errors.fullName}
            </div>
          )}
          <Label
            htmlFor="email"
            className="m-auto mr-0 text-base font-semibold"
          >
            <span>
              {t("Email")}
              <span className="ml-0.5 text-red-500 opacity-70">*</span>
            </span>
          </Label>
          <Input
            type="text"
            className="w-full p-1 ps-2"
            id="email"
            placeholder={t("Enter email")}
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            autoCapitalize="off"
            required
          />
          {errors.email && (
            <div className="col-span-2 -mt-2 text-sm text-red-500">
              {errors.email}
            </div>
          )}
          <Label
            htmlFor="password"
            className="m-auto mr-0 text-base font-semibold"
          >
            <span>
              {t("Password")}
              <span className="ml-0.5 text-red-500 opacity-70">*</span>
            </span>
          </Label>
          <Input
            type="password"
            className="w-full p-1 ps-2"
            id="password"
            placeholder={t("Enter password")}
            value={newUser.password}
            onChange={(e) =>
              setNewUser({ ...newUser, password: e.target.value })
            }
            autoCapitalize="off"
            required
          />
          {errors.password && (
            <div className="col-span-2 -mt-2 text-sm text-red-500">
              {errors.password}
            </div>
          )}
          <Label
            htmlFor="phoneNumber"
            className="m-auto mr-0 text-base font-semibold"
          >
            {t("Phone Number")}
          </Label>
          <Input
            type="text"
            className="w-full p-1 ps-2"
            id="phoneNumber"
            placeholder={t("Enter phone number")}
            value={newUser.phoneNumber}
            onChange={(e) =>
              setNewUser({ ...newUser, phoneNumber: e.target.value })
            }
            autoCapitalize="off"
          />
          {errors.phoneNumber && (
            <div className="col-span-2 -mt-2 text-sm text-red-500">
              {errors.phoneNumber}
            </div>
          )}
          <Label
            htmlFor="companyName"
            className="m-auto mr-0 text-base font-semibold"
          >
            {t("Company Name")}
          </Label>
          <Input
            type="text"
            className="w-full p-1 ps-2"
            id="companyName"
            placeholder={t("Enter company name")}
            value={newUser.companyName}
            onChange={(e) =>
              setNewUser({ ...newUser, companyName: e.target.value })
            }
            autoCapitalize="off"
          />
          {errors.companyName && (
            <div className="col-span-2 -mt-2 text-sm text-red-500">
              {errors.companyName}
            </div>
          )}
          <Label
            htmlFor="authMethod"
            className="m-auto mr-0 text-base font-semibold"
          >
            {t("Authentication Method")}
          </Label>
          <Select
            value={newUser.authMethod}
            onValueChange={(value) =>
              setNewUser({
                ...newUser,
                authMethod: value as typeof newUser.authMethod,
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue className="w-full">
                {t(newUser.authMethod)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t("Authentication Method")}</SelectLabel>
                {AUTH_METHODS.map((method) => (
                  <SelectItem key={method} value={method}>
                    {t(method)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {errors.authMethod && (
            <div className="col-span-2 -mt-2 text-sm text-red-500">
              {errors.authMethod}
            </div>
          )}
        </div>
        <Button disabled={submitting} onClick={handleRegister} className="mt-5">
          {submitting ? t("Registering...") : t("Register")}
        </Button>
      </div>
    </>
  ) : (
    <div className="text-center">
      <h2 className="text-xl font-semibold">{t("Registration Successful")}</h2>
      <p className="mt-2">
        {t("Please check your email for further instructions.")}
      </p>
    </div>
  );
};

export default Register;
